import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Clock, Loader2, Plus, Calendar, History, Users, FileText,
  Cake, AlertTriangle, Check, X, Eye, CheckCircle, XCircle,
  Gift, Send, PenTool, User, Shield, Trash2, Search, RefreshCw,
  ChevronLeft, ChevronRight, Palmtree, CalendarDays, FileSpreadsheet
} from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FirmasDisplay, FirmarSolicitudButton, AprobarConFirmaDialog } from "../components/FirmaDigital";

const TIPOS_SOLICITUD = [
  { id: "permiso", label: "Permisos", icon: FileText, color: "bg-blue-500", description: "Solicitar permiso laboral" },
  { id: "ausencia_justificada", label: "Ausencia Justificada", icon: AlertTriangle, color: "bg-green-500", description: "Reportar ausencia con justificación" },
  { id: "ausencia_injustificada", label: "Ausencia Injustificada", icon: X, color: "bg-red-500", description: "Registrar ausencia sin justificación" },
  { id: "tardanza", label: "Tardanza", icon: Clock, color: "bg-amber-500", description: "Reportar llegada tarde" },
  { id: "cumpleanos", label: "Día de Cumpleaños", icon: Cake, color: "bg-pink-500", description: "Solicitar día libre por cumpleaños" }
];

const ESTADOS = [
  { value: "pendiente", label: "Pendiente", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", icon: Clock },
  { value: "aprobada", label: "Aprobada", color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300", icon: CheckCircle },
  { value: "rechazada", label: "Rechazada", color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300", icon: XCircle }
];

export default function SolicitudesUnificadasPage() {
  const [searchParams] = useSearchParams();
  const { solicitudId: urlSolicitudId } = useParams();
  // Read tab from URL or default to "solicitudes"
  const [activeTab, setActiveTab] = useState(() => {
    const urlTab = searchParams.get("tab");
    return urlTab || "solicitudes";
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // All requests data
  const [solicitudesPermisos, setSolicitudesPermisos] = useState([]);
  const [solicitudesCumpleanos, setSolicitudesCumpleanos] = useState([]);
  const [solicitudesTardanzas, setSolicitudesTardanzas] = useState([]);
  const [solicitudesVacaciones, setSolicitudesVacaciones] = useState([]);
  const [tiposPermiso, setTiposPermiso] = useState([]);
  const [saldos, setSaldos] = useState({ permisos: [], vacaciones: [] });
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTipoSolicitud, setSelectedTipoSolicitud] = useState(null);
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [solicitudFirmas, setSolicitudFirmas] = useState(null);
  const [userFirma, setUserFirma] = useState(null);
  const [firmaSolicitud, setFirmaSolicitud] = useState(false); // Whether user has signed the request
  const [allSolicitudesAdmin, setAllSolicitudesAdmin] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  
  // Saldo General y Calendario General (solo Admin/GH)
  const [saldosGeneral, setSaldosGeneral] = useState([]);
  const [eventosCalendario, setEventosCalendario] = useState([]);
  const [loadingSaldosGeneral, setLoadingSaldosGeneral] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [saldosSearchTerm, setSaldosSearchTerm] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarDeptFilter, setCalendarDeptFilter] = useState("all");
  const [calendarDepartamentos, setCalendarDepartamentos] = useState([]);
  
  // User info
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "gestion_humana"].includes(user?.role?.toLowerCase());
  // Detección robusta de Gestión Humana (rol personalizado / con acentos o espacios, o por departamento)
  const _normalizeGH = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[\s_-]+/g, " ").trim();
  const _ghTerms = ["gestion humana", "recursos humanos", "rrhh"];
  const _roleN = _normalizeGH(user?.role);
  const _deptN = _normalizeGH(user?.departamento_nombre);
  const isGestionHumana = _ghTerms.some((t) => _roleN === t || _roleN.includes(t) || _deptN.includes(t));
  // User is supervisor if has is_supervisor flag OR has role "supervisor"
  const isSupervisor = user?.is_supervisor || user?.role?.toLowerCase() === "supervisor";
  // Admin o Gestión Humana: ven TODAS las pestañas (Admin: Todas, Calendario General, etc.)
  const isAdminOrGH = isAdmin || isGestionHumana;
  // Pueden ver "Saldo General": admin + GH + supervisores (estos solo ven sus supervisados)
  const canVerSaldoGeneral = isAdmin || isGestionHumana || isSupervisor;
  
  // Form data
  const [formData, setFormData] = useState({
    // Common fields (auto-filled)
    departamento: "",
    departamento_id: "",
    supervisor: "",
    supervisor_id: "",
    // Permiso fields
    tipo_permiso: "",
    fecha_inicio: "",
    fecha_fin: "",
    hora_inicio: "",
    hora_fin: "",
    dias_solicitados: 1,
    motivo: "",
    requiere_compensacion: false,
    // Cumpleaños fields
    fecha_solicitada: "",
    // Tardanza fields
    tipo_tardanza: "tardanza",
    fecha: new Date().toISOString().split('T')[0],
    hora_entrada: "",
    hora_esperada: "08:00",
    minutos_tarde: 0,
    justificacion: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [permisosRes, cumpleanosRes, tardanzasRes, vacacionesRes, tiposRes, saldosPermisosRes] = await Promise.all([
        axios.get(`${API}/permisos/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/cumpleanos/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/tardanzas`, getAuthHeaders()),
        axios.get(`${API}/vacaciones/solicitudes`, getAuthHeaders()),
        axios.get(`${API}/permisos/tipos`, getAuthHeaders()),
        axios.get(`${API}/permisos/saldos`, getAuthHeaders())
      ]);
      setSolicitudesPermisos(permisosRes.data);
      setSolicitudesCumpleanos(cumpleanosRes.data);
      setSolicitudesTardanzas(tardanzasRes.data);
      setSolicitudesVacaciones(vacacionesRes.data);
      setTiposPermiso(tiposRes.data);
      setSaldos(prev => ({ ...prev, permisos: saldosPermisosRes.data }));
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Fetch all solicitudes for admin
  const fetchAllSolicitudesAdmin = useCallback(async () => {
    if (!isAdminOrGH) return;
    try {
      setLoadingAdmin(true);
      const res = await axios.get(`${API}/gestion-humana/todas-solicitudes`, getAuthHeaders());
      setAllSolicitudesAdmin(res.data.solicitudes || []);
    } catch (err) {
      console.error("Error loading admin solicitudes:", err);
    } finally {
      setLoadingAdmin(false);
    }
  }, [getAuthHeaders, isAdminOrGH]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open solicitud from URL param (from email link)
  // Importante: marcar el `tipo_solicitud` al combinar, porque los endpoints
  // individuales no lo devuelven y el modal de detalle decide qué bloque mostrar
  // (Permiso / Cumpleaños / Tardanza / Vacaciones) en base a este flag.
  const combinedSolicitudes = useMemo(() => [
    ...solicitudesPermisos.map(s => ({ ...s, tipo_solicitud: s.tipo_solicitud || "permiso" })),
    ...solicitudesVacaciones.map(s => ({ ...s, tipo_solicitud: s.tipo_solicitud || "vacaciones" })),
    ...solicitudesCumpleanos.map(s => ({ ...s, tipo_solicitud: s.tipo_solicitud || "cumpleanos" })),
    ...solicitudesTardanzas.map(s => ({ ...s, tipo_solicitud: s.tipo_solicitud || (s.tipo === "tardanza" ? "tardanza" : "ausencia") }))
  ], [solicitudesPermisos, solicitudesVacaciones, solicitudesCumpleanos, solicitudesTardanzas]);

  useEffect(() => {
    if (urlSolicitudId && combinedSolicitudes.length > 0) {
      const sol = combinedSolicitudes.find(s => s.id === urlSolicitudId);
      if (sol) {
        setSelectedSolicitud(sol);
        setShowDetailDialog(true);
      }
    }
  }, [urlSolicitudId, combinedSolicitudes]);


  // Load admin data when tab changes to admin
  useEffect(() => {
    if (activeTab === "admin" && isAdminOrGH) {
      fetchAllSolicitudesAdmin();
    }
  }, [activeTab, isAdminOrGH, fetchAllSolicitudesAdmin]);
  
  // Fetch Saldo General (Admin/GH only)
  const exportarSaldosExcel = async () => {
    try {
      const res = await axios.get(`${API}/gestion-humana/saldos-general/excel`, { ...getAuthHeaders(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `saldo_general_vacaciones_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch { toast.error("Error al exportar el Excel"); }
  };


  const fetchSaldosGeneral = useCallback(async (showToast = false) => {
    if (!canVerSaldoGeneral) return;
    try {
      setLoadingSaldosGeneral(true);
      const res = await axios.get(`${API}/gestion-humana/saldos-general`, getAuthHeaders());
      setSaldosGeneral(res.data.saldos || []);
      if (showToast) {
        toast.success("Saldos actualizados correctamente", {
          description: `Se recalcularon los saldos de ${res.data.saldos?.length || 0} usuarios`
        });
      }
    } catch (err) {
      console.error("Error fetching saldos general:", err);
      if (showToast) {
        toast.error("Error al actualizar saldos");
      }
    } finally {
      setLoadingSaldosGeneral(false);
    }
  }, [getAuthHeaders, canVerSaldoGeneral]);
  
  // Fetch Calendario General (Admin/GH only)
  const fetchEventosCalendario = useCallback(async () => {
    if (!isAdminOrGH) return;
    try {
      setLoadingEventos(true);
      const res = await axios.get(`${API}/gestion-humana/calendario-general`, getAuthHeaders());
      setEventosCalendario(res.data.eventos || []);
      setCalendarDepartamentos(res.data.departamentos || []);
    } catch (err) {
      console.error("Error fetching eventos calendario:", err);
    } finally {
      setLoadingEventos(false);
    }
  }, [getAuthHeaders, isAdminOrGH]);
  
  // Load saldos/calendario when their tabs are active
  useEffect(() => {
    if (activeTab === "saldos_general" && canVerSaldoGeneral) {
      fetchSaldosGeneral();
    } else if (activeTab === "calendario_general" && isAdminOrGH) {
      fetchEventosCalendario();
    }
  }, [activeTab, isAdminOrGH, canVerSaldoGeneral, fetchSaldosGeneral, fetchEventosCalendario]);

  // Delete solicitud function
  const handleDeleteSolicitud = async (solicitud) => {
    if (!solicitud) return;
    
    try {
      let endpoint = "";
      const tipoSolicitud = solicitud.tipo_solicitud;
      
      if (tipoSolicitud === "permiso") {
        endpoint = `${API}/permisos/solicitudes/${solicitud.id}`;
      } else if (tipoSolicitud === "vacaciones") {
        endpoint = `${API}/vacaciones/solicitudes/${solicitud.id}`;
      } else if (tipoSolicitud === "cumpleanos") {
        endpoint = `${API}/cumpleanos/solicitudes/${solicitud.id}`;
      } else if (["tardanza", "ausencia_justificada", "ausencia_injustificada"].includes(tipoSolicitud)) {
        endpoint = `${API}/tardanzas/${solicitud.id}`;
      } else {
        toast.error("Tipo de solicitud no soportado para eliminación");
        return;
      }
      
      await axios.delete(endpoint, getAuthHeaders());
      toast.success("Solicitud eliminada correctamente");
      setShowDeleteDialog(false);
      setSelectedSolicitud(null);
      
      // Refresh data
      fetchData();
      if (isAdminOrGH) {
        fetchAllSolicitudesAdmin();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar la solicitud");
    }
  };

  // Load user's signature
  useEffect(() => {
    const loadUserFirma = async () => {
      try {
        const res = await axios.get(`${API}/firma-digital/mi-firma`, getAuthHeaders());
        setUserFirma(res.data?.firma_url || null);
      } catch (err) {
        setUserFirma(null);
      }
    };
    loadUserFirma();
  }, [getAuthHeaders]);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        departamento: user.departamento_nombre || "",
        departamento_id: user.departamento_id || "",
        supervisor: user.supervisor_nombre || "",
        supervisor_id: user.supervisor_id || ""
      }));
    }
  }, [user]);

  const openNewSolicitud = (tipo) => {
    setSelectedTipoSolicitud(tipo);
    setFirmaSolicitud(false); // Reset signature when opening new dialog
    setShowNewDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has signed the request
    if (!firmaSolicitud) {
      toast.error("Debe firmar la solicitud antes de enviarla");
      return;
    }
    
    setSaving(true);

    try {
      let solicitudId = null;
      
      if (selectedTipoSolicitud === "permiso") {
        if (!formData.tipo_permiso || !formData.fecha_inicio) {
          toast.error("Complete los campos requeridos");
          setSaving(false);
          return;
        }
        
        // Validar campos de horas obligatorios
        if (!formData.hora_inicio || !formData.hora_fin) {
          toast.error("Los campos de hora inicio y hora fin son obligatorios");
          setSaving(false);
          return;
        }
        
        // Calcular horas solicitadas
        const [startH, startM] = formData.hora_inicio.split(':').map(Number);
        const [endH, endM] = formData.hora_fin.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        const horasCalc = (endMinutes - startMinutes) / 60;
        
        if (horasCalc <= 0) {
          toast.error("La hora de fin debe ser mayor a la hora de inicio");
          setSaving(false);
          return;
        }
        
        // Validar máximo 4 horas
        if (horasCalc > 4) {
          toast.error("El permiso es máximo 4 horas. Si necesita más tiempo, debe solicitar un día de vacaciones.");
          setSaving(false);
          return;
        }
        
        const res = await axios.post(`${API}/permisos/solicitudes`, {
          tipo_permiso: formData.tipo_permiso,
          fecha_inicio: formData.fecha_inicio,
          fecha_fin: formData.fecha_inicio, // Para permisos por horas, fecha_fin = fecha_inicio
          hora_inicio: formData.hora_inicio,
          hora_fin: formData.hora_fin,
          horas_solicitadas: horasCalc,
          dias_solicitados: 0,
          motivo: formData.motivo,
          requiere_compensacion: formData.requiere_compensacion
        }, getAuthHeaders());
        solicitudId = res.data?.id;
        
        // Sign the request after creation
        if (solicitudId && userFirma) {
          await axios.post(`${API}/firma-digital/firmar-solicitud`, {
            solicitud_id: solicitudId,
            tipo_solicitud: "permiso",
            usar_firma_guardada: true
          }, getAuthHeaders());
        }
        
        toast.success("Solicitud de permiso creada y firmada");
      } else if (selectedTipoSolicitud === "cumpleanos") {
        if (!formData.fecha_solicitada) {
          toast.error("Seleccione la fecha");
          setSaving(false);
          return;
        }
        const res = await axios.post(`${API}/cumpleanos/solicitudes`, {
          fecha_solicitada: formData.fecha_solicitada,
          motivo: "Día de cumpleaños"
        }, getAuthHeaders());
        solicitudId = res.data?.id;
        
        // Sign the request
        if (solicitudId && userFirma) {
          await axios.post(`${API}/firma-digital/firmar-solicitud`, {
            solicitud_id: solicitudId,
            tipo_solicitud: "cumpleanos",
            usar_firma_guardada: true
          }, getAuthHeaders());
        }
        
        toast.success("Solicitud de día de cumpleaños creada y firmada");
      } else if (selectedTipoSolicitud === "tardanza") {
        // This is a self-report of tardiness
        const res = await axios.post(`${API}/tardanzas`, {
          tipo: "tardanza",
          empleado_id: user?.empleado_id || user?.id,
          fecha: formData.fecha,
          hora_entrada: formData.hora_entrada,
          hora_esperada: formData.hora_esperada,
          minutos_tarde: formData.minutos_tarde,
          motivo: formData.motivo,
          justificacion: formData.justificacion
        }, getAuthHeaders());
        solicitudId = res.data?.id;
        
        // Sign the request
        if (solicitudId && userFirma) {
          await axios.post(`${API}/firma-digital/firmar-solicitud`, {
            solicitud_id: solicitudId,
            tipo_solicitud: "tardanza",
            usar_firma_guardada: true
          }, getAuthHeaders());
        }
        
        toast.success("Tardanza reportada y firmada");
      } else if (selectedTipoSolicitud === "ausencia_justificada" || selectedTipoSolicitud === "ausencia_injustificada") {
        // Report absence
        const res = await axios.post(`${API}/tardanzas`, {
          tipo: selectedTipoSolicitud,
          empleado_id: user?.empleado_id || user?.id,
          fecha: formData.fecha,
          hora_entrada: null,
          hora_esperada: formData.hora_esperada,
          minutos_tarde: 0,
          motivo: formData.motivo,
          justificacion: formData.justificacion
        }, getAuthHeaders());
        solicitudId = res.data?.id;
        
        // Sign the request
        if (solicitudId && userFirma) {
          await axios.post(`${API}/firma-digital/firmar-solicitud`, {
            solicitud_id: solicitudId,
            tipo_solicitud: "tardanza",
            usar_firma_guardada: true
          }, getAuthHeaders());
        }
        
        toast.success("Ausencia registrada y firmada");
      }

      setShowNewDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear solicitud");
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData(prev => ({
      ...prev,
      tipo_permiso: "",
      fecha_inicio: "",
      fecha_fin: "",
      hora_inicio: "",
      hora_fin: "",
      dias_solicitados: 1,
      motivo: "",
      requiere_compensacion: false,
      fecha_solicitada: "",
      tipo_tardanza: "tardanza",
      fecha: new Date().toISOString().split('T')[0],
      hora_entrada: "",
      minutos_tarde: 0,
      justificacion: ""
    }));
  };

  const handleApprove = (solicitud, tipo) => {
    // Open the approval dialog with signature requirement
    setSelectedSolicitud({ ...solicitud, tipo_solicitud: tipo });
    setShowApprovalDialog(true);
  };

  // Function to fetch signatures for a solicitud when viewing details
  const fetchFirmas = async (solicitud) => {
    try {
      const res = await axios.get(
        `${API}/firma-digital/solicitud/${solicitud.tipo_solicitud}/${solicitud.id}/firmas`,
        getAuthHeaders()
      );
      setSolicitudFirmas(res.data);
    } catch (err) {
      setSolicitudFirmas(null);
    }
  };

  const openDetailDialog = (solicitud) => {
    setSelectedSolicitud(solicitud);
    fetchFirmas(solicitud);
    setShowDetailDialog(true);
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const calculateMinutesLate = (entrada, esperada) => {
    if (!entrada || !esperada) return 0;
    const [hE, mE] = entrada.split(':').map(Number);
    const [hEsp, mEsp] = esperada.split(':').map(Number);
    const totalEntrada = hE * 60 + mE;
    const totalEsperada = hEsp * 60 + mEsp;
    return Math.max(0, totalEntrada - totalEsperada);
  };

  // Combine all requests for history
  const allSolicitudes = [
    ...solicitudesPermisos.map(s => ({ ...s, tipo_solicitud: "permiso", tipo_label: s.tipo_permiso_nombre || "Permiso" })),
    ...solicitudesCumpleanos.map(s => ({ ...s, tipo_solicitud: "cumpleanos", tipo_label: "Día de Cumpleaños" })),
    ...solicitudesVacaciones.map(s => ({ ...s, tipo_solicitud: "vacaciones", tipo_label: "Vacaciones" })),
    ...solicitudesTardanzas.map(s => {
      // Determine label based on tipo
      let tipo_label = "Tardanza";
      if (s.tipo === "ausencia_justificada") tipo_label = "Ausencia Justificada";
      else if (s.tipo === "ausencia_injustificada") tipo_label = "Ausencia Injustificada";
      else if (s.tipo === "tardanza") tipo_label = "Tardanza";
      
      return { 
        ...s, 
        tipo_solicitud: s.tipo || "tardanza", 
        tipo_label,
        estado: s.estado || "registrada"
      };
    })
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Filter for user's own requests
  const misSolicitudes = allSolicitudes.filter(s => s.user_id === user?.id || s.empleado_id === user?.id);

  // Filter for supervised employees (for supervisors and admin)
  // The backend already returns only solicitudes the user can see (their own + recursively supervised)
  // So we just need to filter out the user's own solicitudes
  const solicitudesSupervisados = allSolicitudes.filter(s => {
    // Exclude user's own solicitudes
    if (s.user_id === user?.id) return false;
    if (s.empleado_id === user?.id) return false;
    
    // All other solicitudes are from supervised users (backend already filtered)
    return true;
  });

  const getTipoPermisoLabel = (tipoId) => {
    const tipo = tiposPermiso.find(t => t.id === tipoId);
    return tipo?.nombre || tipoId;
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              Solicitudes
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Gestiona tus solicitudes de permisos, cumpleaños y tardanzas
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white dark:bg-slate-800 border dark:border-slate-700 p-1 mb-6 flex-wrap">
              <TabsTrigger value="solicitudes" data-testid="tab-solicitudes" className="gap-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 dark:data-[state=active]:bg-blue-900/30">
                <Clock className="w-4 h-4" />
                Solicitudes
              </TabsTrigger>
              <TabsTrigger value="historial" data-testid="tab-mi-historial" className="gap-2">
                <History className="w-4 h-4" />
                Mi Historial
              </TabsTrigger>
              {(isAdminOrGH || isSupervisor) && (
                <TabsTrigger value="supervisados" className="gap-2 relative" data-testid="tab-supervisados">
                  <Users className="w-4 h-4" />
                  Historial Supervisados
                  {solicitudesSupervisados.filter(s => s.estado === "pendiente").length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {solicitudesSupervisados.filter(s => s.estado === "pendiente").length}
                    </span>
                  )}
                </TabsTrigger>
              )}
              {isAdminOrGH && (
                <TabsTrigger value="admin" className="gap-2 bg-purple-50 data-[state=active]:bg-purple-100 data-[state=active]:text-purple-700 dark:bg-purple-900/20 dark:data-[state=active]:bg-purple-900/40" data-testid="tab-admin">
                  <Shield className="w-4 h-4" />
                  Admin: Todas
                </TabsTrigger>
              )}
              <TabsTrigger value="saldos" className="gap-2">
                <Users className="w-4 h-4" />
                Saldos
              </TabsTrigger>
              <TabsTrigger value="calendario" data-testid="tab-calendario" className="gap-2">
                <Calendar className="w-4 h-4" />
                Calendario
              </TabsTrigger>
              {canVerSaldoGeneral && (
                <TabsTrigger value="saldos_general" className="gap-2 bg-emerald-50 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700 dark:bg-emerald-900/20 dark:data-[state=active]:bg-emerald-900/40" data-testid="tab-saldo-general">
                  <Palmtree className="w-4 h-4" />
                  Saldo General
                </TabsTrigger>
              )}
              {isAdminOrGH && (
                <>
                  <TabsTrigger value="calendario_general" data-testid="tab-calendario-general" className="gap-2 bg-emerald-50 data-[state=active]:bg-emerald-100 data-[state=active]:text-emerald-700 dark:bg-emerald-900/20 dark:data-[state=active]:bg-emerald-900/40">
                    <CalendarDays className="w-4 h-4" />
                    Calendario General
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            {/* Solicitudes Tab - Create new requests */}
            <TabsContent value="solicitudes">
              {/* User Info Card */}
              <Card className="dark:bg-slate-800 dark:border-slate-700 mb-6">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Nombre</p>
                      <p className="font-medium text-slate-900 dark:text-white">{user?.name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Departamento</p>
                      <p className="font-medium text-slate-900 dark:text-white">{user?.departamento_nombre || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Supervisor</p>
                      <p className="font-medium text-slate-900 dark:text-white">{user?.supervisor_nombre || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Cargo</p>
                      <p className="font-medium text-slate-900 dark:text-white">{user?.cargo || user?.role || "-"}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Request Type Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {TIPOS_SOLICITUD.map(tipo => {
                  const Icon = tipo.icon;
                  return (
                    <Card 
                      key={tipo.id}
                      className="dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => openNewSolicitud(tipo.id)}
                      data-testid={`btn-nueva-${tipo.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          <div className={`p-4 rounded-xl ${tipo.color}`}>
                            <Icon className="w-8 h-8 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-slate-900 dark:text-white">{tipo.label}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">{tipo.description}</p>
                          </div>
                          <Plus className="w-6 h-6 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Pending requests for supervisors */}
              {(isAdminOrGH || isSupervisor) && solicitudesSupervisados.filter(s => s.estado === "pendiente").length > 0 && (
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Solicitudes Pendientes de Supervisados
                    </CardTitle>
                    <CardDescription>
                      Tienes {solicitudesSupervisados.filter(s => s.estado === "pendiente").length} solicitudes pendientes de aprobar
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {solicitudesSupervisados.filter(s => s.estado === "pendiente").map(sol => (
                        <div 
                          key={sol.id}
                          className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <Badge className={
                              sol.tipo_solicitud === "permiso" ? "bg-blue-100 text-blue-700" :
                              sol.tipo_solicitud === "cumpleanos" ? "bg-pink-100 text-pink-700" :
                              "bg-amber-100 text-amber-700"
                            }>
                              {sol.tipo_label}
                            </Badge>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-slate-900 dark:text-white">{sol.user_name}</p>
                                {sol.user_id !== user?.id && (
                                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700">
                                    <Users className="w-3 h-3 mr-1" />
                                    Supervisado{sol.created_by_name && sol.created_by_name !== sol.user_name ? `: ${sol.created_by_name}` : ''}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{sol.user_departamento}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-blue-600 border-blue-200"
                              onClick={() => handleApprove(sol, sol.tipo_solicitud)}
                            >
                              <PenTool className="w-4 h-4 mr-1" />
                              Responder
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => openDetailDialog(sol)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Mi Historial Tab */}
            <TabsContent value="historial">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle>Mi Historial de Solicitudes</CardTitle>
                  <CardDescription>
                    Todas tus solicitudes realizadas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : misSolicitudes.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      No tienes solicitudes registradas
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 dark:bg-slate-900">
                          <tr>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Tipo</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Fecha</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Detalle</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                            <th className="text-right p-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y dark:divide-slate-700">
                          {misSolicitudes.map(sol => {
                            const estado = ESTADOS.find(e => e.value === sol.estado) || { label: sol.estado, color: "bg-slate-100 text-slate-700" };
                            return (
                              <tr key={`${sol.tipo_solicitud}-${sol.id}`} className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="p-4">
                                  <Badge className={
                                    sol.tipo_solicitud === "permiso" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" :
                                    sol.tipo_solicitud === "cumpleanos" ? "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300" :
                                    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                  }>
                                    {sol.tipo_label}
                                  </Badge>
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {new Date(sol.created_at).toLocaleDateString()}
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {sol.tipo_solicitud === "permiso" && (
                                    <>
                                      {sol.fecha_inicio} - {sol.fecha_fin}
                                      {(sol.hora_inicio || sol.hora_fin) && (
                                        <span className="block text-xs text-slate-500">
                                          {sol.hora_inicio || "--:--"} a {sol.hora_fin || "--:--"}
                                        </span>
                                      )}
                                    </>
                                  )}
                                  {sol.tipo_solicitud === "cumpleanos" && sol.fecha_solicitada}
                                  {sol.tipo_solicitud === "tardanza" && `${sol.fecha} - ${sol.hora_entrada || "N/A"}`}
                                  {sol.tipo_solicitud === "vacaciones" && `${sol.fecha_inicio || ""} - ${sol.fecha_fin || ""} (${sol.dias_solicitados || 0} días)`}
                                </td>
                                <td className="p-4">
                                  <Badge className={estado.color}>{estado.label}</Badge>
                                </td>
                                <td className="p-4 text-right">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => { setSelectedSolicitud(sol); setShowDetailDialog(true); }}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Historial Supervisados Tab - For supervisors and admins */}
            <TabsContent value="supervisados">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    Solicitudes de Usuarios Supervisados
                  </CardTitle>
                  <CardDescription>
                    Revisa y gestiona las solicitudes de los usuarios que supervisas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {solicitudesSupervisados.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 dark:text-slate-400">
                        No hay solicitudes de usuarios supervisados
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b dark:border-slate-700">
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Empleado</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Tipo</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Detalles</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Fecha Solicitud</th>
                            <th className="text-left p-4 font-medium text-slate-600 dark:text-slate-300">Estado</th>
                            <th className="text-right p-4 font-medium text-slate-600 dark:text-slate-300">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {solicitudesSupervisados.sort((a, b) => {
                            // Sort pending first, then by date
                            if (a.estado === "pendiente" && b.estado !== "pendiente") return -1;
                            if (a.estado !== "pendiente" && b.estado === "pendiente") return 1;
                            return new Date(b.created_at) - new Date(a.created_at);
                          }).map(sol => {
                            const estado = ESTADOS.find(e => e.value === sol.estado) || ESTADOS[0];
                            const Icon = estado.icon;
                            return (
                              <tr 
                                key={sol.id} 
                                className={`border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 ${sol.estado === "pendiente" ? "bg-amber-50/50 dark:bg-amber-900/10" : ""}`}
                              >
                                <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                      <User className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-slate-900 dark:text-white">{sol.user_name || sol.empleado_nombre || "Usuario"}</p>
                                      <p className="text-xs text-slate-500">{sol.departamento_nombre || sol.user_departamento || sol.empleado_departamento || "-"}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4">
                                  <Badge className={
                                    sol.tipo_solicitud === "permiso" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" :
                                    sol.tipo_solicitud === "cumpleanos" ? "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300" :
                                    sol.tipo_solicitud === "vacaciones" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
                                    sol.tipo_solicitud === "ausencia_justificada" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" :
                                    sol.tipo_solicitud === "ausencia_injustificada" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" :
                                    sol.tipo_solicitud === "tardanza" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" :
                                    "bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300"
                                  }>
                                    {sol.tipo_label || sol.tipo_solicitud}
                                  </Badge>
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {sol.tipo_solicitud === "permiso" && `${sol.fecha_inicio || ""} - ${sol.fecha_fin || ""}`}
                                  {sol.tipo_solicitud === "cumpleanos" && `Día: ${sol.fecha_solicitada || sol.fecha_cumpleanos || ""}`}
                                  {sol.tipo_solicitud === "vacaciones" && `${sol.fecha_inicio || ""} - ${sol.fecha_fin || ""} (${sol.dias_solicitados || 0} días)`}
                                  {(sol.tipo_solicitud === "tardanza" || sol.tipo_solicitud === "ausencia_justificada" || sol.tipo_solicitud === "ausencia_injustificada") && 
                                    `${sol.fecha || ""} ${sol.hora_entrada ? `- ${sol.hora_entrada}` : ""}`
                                  }
                                </td>
                                <td className="p-4 text-slate-600 dark:text-slate-300">
                                  {new Date(sol.created_at).toLocaleDateString('es-ES', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </td>
                                <td className="p-4">
                                  <Badge className={estado.color}>
                                    <Icon className="w-3 h-3 mr-1" />
                                    {estado.label}
                                  </Badge>
                                </td>
                                <td className="p-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      onClick={() => { setSelectedSolicitud(sol); setShowDetailDialog(true); }}
                                      title="Ver detalles"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    {/* Show respond button for pending, or change status button for processed */}
                                    {sol.estado === "pendiente" ? (
                                      <Button 
                                        size="sm" 
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={() => {
                                          setSelectedSolicitud(sol);
                                          handleApprove(sol, sol.tipo_solicitud);
                                        }}
                                        title="Responder"
                                      >
                                        <PenTool className="w-4 h-4 mr-1" />
                                        Responder
                                      </Button>
                                    ) : (isAdminOrGH || isSupervisor) && (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                                        onClick={() => {
                                          setSelectedSolicitud(sol);
                                          handleApprove(sol, sol.tipo_solicitud);
                                        }}
                                        title="Cambiar estado"
                                      >
                                        <PenTool className="w-4 h-4 mr-1" />
                                        Cambiar Estado
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Saldos Tab */}
            <TabsContent value="saldos">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-500" />
                      Saldo de Permisos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {saldos.permisos.length === 0 ? (
                      <p className="text-slate-500">No hay saldos registrados</p>
                    ) : (
                      <div className="space-y-4">
                        {saldos.permisos.filter(s => s.user_id === user?.id).map(saldo => (
                          <div key={saldo.id} className="flex justify-between items-center p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900 dark:text-white">
                                {getTipoPermisoLabel(saldo.tipo_permiso)}
                              </p>
                              <p className="text-sm text-slate-500">Año {saldo.year}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-blue-600">
                                {saldo.dias_totales - saldo.dias_usados}
                              </p>
                              <p className="text-xs text-slate-500">de {saldo.dias_totales} días</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-green-500" />
                      Saldo de Vacaciones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-500">Ver saldo en módulo de Vacaciones</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Calendario Tab */}
            <TabsContent value="calendario">
              <Card className="dark:bg-slate-800 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    Calendario de Solicitudes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-500 text-center py-8">
                    Vista de calendario próximamente
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Admin: All Requests Tab */}
            {isAdminOrGH && (
              <TabsContent value="admin">
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-purple-500" />
                          Todas las Solicitudes (Admin)
                        </CardTitle>
                        <CardDescription>
                          Vista completa de todas las solicitudes de Gestión Humana con opción de eliminar
                        </CardDescription>
                      </div>
                      <Button variant="outline" size="sm" onClick={fetchAllSolicitudesAdmin}>
                        <Loader2 className={`w-4 h-4 mr-2 ${loadingAdmin ? 'animate-spin' : ''}`} />
                        Actualizar
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingAdmin ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                      </div>
                    ) : allSolicitudesAdmin.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No hay solicitudes</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b dark:border-slate-700">
                              <th className="text-left py-3 px-2">Tipo</th>
                              <th className="text-left py-3 px-2">Empleado</th>
                              <th className="text-left py-3 px-2">Descripción</th>
                              <th className="text-left py-3 px-2">Fecha Inicio</th>
                              <th className="text-left py-3 px-2">Estado</th>
                              <th className="text-left py-3 px-2">Creado</th>
                              <th className="text-center py-3 px-2">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allSolicitudesAdmin.map((sol) => (
                              <tr key={sol.id} className="border-b dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                <td className="py-3 px-2">
                                  <Badge className={`text-xs ${
                                    sol.tipo_solicitud === "permiso" ? "bg-blue-100 text-blue-700" :
                                    sol.tipo_solicitud === "vacaciones" ? "bg-green-100 text-green-700" :
                                    sol.tipo_solicitud === "cumpleanos" ? "bg-pink-100 text-pink-700" :
                                    sol.tipo_solicitud === "tardanza" ? "bg-amber-100 text-amber-700" :
                                    sol.tipo_solicitud === "ausencia_justificada" ? "bg-orange-100 text-orange-700" :
                                    "bg-red-100 text-red-700"
                                  }`}>
                                    {sol.tipo_solicitud === "permiso" ? "Permiso" :
                                     sol.tipo_solicitud === "vacaciones" ? "Vacaciones" :
                                     sol.tipo_solicitud === "cumpleanos" ? "Cumpleaños" :
                                     sol.tipo_solicitud === "tardanza" ? "Tardanza" :
                                     sol.tipo_solicitud === "ausencia_justificada" ? "Ausencia Just." :
                                     sol.tipo_solicitud === "ausencia_injustificada" ? "Ausencia Injust." :
                                     sol.tipo_solicitud}
                                  </Badge>
                                </td>
                                <td className="py-3 px-2">
                                  <div>
                                    <p className="font-medium">{sol.user_name || sol.empleado_nombre || "N/A"}</p>
                                    <p className="text-xs text-slate-500">{sol.user_email || ""}</p>
                                  </div>
                                </td>
                                <td className="py-3 px-2">
                                  <p className="text-slate-600 dark:text-slate-300">{sol.descripcion || sol.motivo || sol.justificacion || "-"}</p>
                                </td>
                                <td className="py-3 px-2">
                                  {sol.fecha_inicio || sol.fecha_solicitada || sol.fecha || "-"}
                                </td>
                                <td className="py-3 px-2">
                                  <Badge className={`text-xs ${
                                    sol.estado === "pendiente" ? "bg-amber-100 text-amber-700" :
                                    sol.estado === "aprobada" ? "bg-green-100 text-green-700" :
                                    sol.estado === "rechazada" ? "bg-red-100 text-red-700" :
                                    "bg-slate-100 text-slate-700"
                                  }`}>
                                    {sol.estado || "N/A"}
                                  </Badge>
                                </td>
                                <td className="py-3 px-2 text-xs text-slate-500">
                                  {sol.created_at ? new Date(sol.created_at).toLocaleDateString('es-DO') : "-"}
                                </td>
                                <td className="py-3 px-2">
                                  <div className="flex items-center justify-center gap-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setSelectedSolicitud(sol);
                                        setShowDetailDialog(true);
                                      }}
                                    >
                                      <Eye className="w-4 h-4 text-blue-500" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => {
                                        setSelectedSolicitud(sol);
                                        setShowDeleteDialog(true);
                                      }}
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
            
            {/* Saldo General Tab - Admin/GH only */}
            {canVerSaldoGeneral && (
              <TabsContent value="saldos_general">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Palmtree className="w-5 h-5 text-emerald-600" />
                        Saldo General de Vacaciones y Permisos
                      </CardTitle>
                      <CardDescription>{isAdminOrGH ? "Vista consolidada de todos los usuarios" : "Vista consolidada de sus supervisados"}</CardDescription>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="default"
                        className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        onClick={exportarSaldosExcel}
                        data-testid="saldos-excel-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Exportar Excel
                      </Button>
                      <Button 
                      variant="default" 
                      size="default"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => fetchSaldosGeneral(true)}
                      disabled={loadingSaldosGeneral}
                    >
                      {loadingSaldosGeneral ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Actualizando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Actualizar Saldos
                        </>
                      )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        data-testid="saldos-search-input"
                        placeholder="Buscar por nombre o email..."
                        value={saldosSearchTerm}
                        onChange={(e) => setSaldosSearchTerm(e.target.value)}
                        className="pl-10 max-w-sm"
                      />
                    </div>
                    {loadingSaldosGeneral ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                      </div>
                    ) : (
                      <div className="rounded-lg border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-100 dark:bg-slate-800">
                              <TableHead>Usuario</TableHead>
                              <TableHead>Departamento</TableHead>
                              <TableHead className="text-center">Días Totales</TableHead>
                              <TableHead className="text-center">Vac. Tomadas</TableHead>
                              <TableHead className="text-center">Vac. Pendientes</TableHead>
                              <TableHead className="text-center">Perm. Restantes</TableHead>
                              <TableHead className="text-center">Perm. Tomados</TableHead>
                              <TableHead className="text-center">Perm. Pendientes</TableHead>
                              <TableHead className="text-center">Día Cumpleaños</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {saldosGeneral
                              .filter((s) => {
                                if (!saldosSearchTerm) return true;
                                const term = saldosSearchTerm.toLowerCase();
                                return (s.nombre || "").toLowerCase().includes(term) || (s.email || "").toLowerCase().includes(term);
                              })
                              .map((s) => (
                              <TableRow key={s.user_id}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                      <User className="w-4 h-4 text-emerald-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium">{s.nombre}</p>
                                      <p className="text-xs text-slate-500">{s.email}</p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">{s.departamento || "Sin asignar"}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={s.dias_totales > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                                    {s.dias_totales ?? 0} días
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className="bg-blue-100 text-blue-700">
                                    {s.vacaciones_tomadas || 0} días
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={s.vacaciones_pendientes > 0 ? "bg-yellow-100 text-yellow-700" : "bg-slate-100 text-slate-600"}>
                                    {s.vacaciones_pendientes || 0} días
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={s.permisos_restantes > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                                    {s.permisos_restantes ?? s.permisos_max ?? 6}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className="bg-blue-100 text-blue-700">
                                    {s.permisos_tomados || 0}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={s.permisos_pendientes > 0 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"}>
                                    {s.permisos_pendientes || 0}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge className={s.dia_cumpleanos === "Sí" ? "bg-pink-100 text-pink-700" : "bg-slate-100 text-slate-600"}>
                                    {s.dia_cumpleanos || "No"}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                            {saldosGeneral.length === 0 && !saldosSearchTerm && (
                              <TableRow>
                                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                                  No hay datos de saldos disponibles
                                </TableCell>
                              </TableRow>
                            )}
                            {saldosGeneral.length > 0 && saldosSearchTerm && saldosGeneral.filter((s) => {
                              const term = saldosSearchTerm.toLowerCase();
                              return (s.nombre || "").toLowerCase().includes(term) || (s.email || "").toLowerCase().includes(term);
                            }).length === 0 && (
                              <TableRow>
                                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                                  No se encontraron resultados para "{saldosSearchTerm}"
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
            
            {/* Calendario General Tab - Admin/GH only */}
            {isAdminOrGH && (
              <TabsContent value="calendario_general">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-emerald-600" />
                        Calendario General de Ausencias
                      </CardTitle>
                      <CardDescription>Vacaciones y permisos de todos los colaboradores</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="font-medium min-w-[140px] text-center">
                        {calendarMonth.toLocaleString('es-DO', { month: 'long', year: 'numeric' })}
                      </span>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={fetchEventosCalendario}
                        disabled={loadingEventos}
                        className="ml-2"
                      >
                        {loadingEventos ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Department Filter + Legend */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-yellow-500"></div>
                          <span className="text-sm">Vacaciones (±5 días)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-red-500"></div>
                          <span className="text-sm">Vacaciones (fuera de rango)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded bg-orange-500"></div>
                          <span className="text-sm">Permisos</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">Departamento:</Label>
                        <Select value={calendarDeptFilter} onValueChange={setCalendarDeptFilter}>
                          <SelectTrigger className="w-[200px] dark:bg-slate-700 dark:border-slate-600" data-testid="calendar-dept-filter">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los departamentos</SelectItem>
                            {calendarDepartamentos.map(dept => (
                              <SelectItem key={dept.id} value={dept.id}>{dept.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {loadingEventos ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                      </div>
                    ) : (
                      <div className="rounded-lg border overflow-hidden">
                        {/* Calendar Header */}
                        <div className="grid grid-cols-7 bg-slate-100 dark:bg-slate-800">
                          {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map(day => (
                            <div key={day} className="p-2 text-center font-medium text-sm border-b">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        {/* Calendar Grid */}
                        <div className="grid grid-cols-7">
                          {(() => {
                            const year = calendarMonth.getFullYear();
                            const month = calendarMonth.getMonth();
                            const firstDay = new Date(year, month, 1);
                            const lastDay = new Date(year, month + 1, 0);
                            const daysInMonth = lastDay.getDate();
                            const startDayOfWeek = firstDay.getDay();
                            
                            const days = [];
                            for (let i = 0; i < startDayOfWeek; i++) days.push(null);
                            for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
                            
                            return days.map((day, idx) => {
                              const dayEvents = day ? eventosCalendario.filter(e => {
                                const dateStr = day.toISOString().split('T')[0];
                                const inicio = e.fecha_inicio?.split('T')[0];
                                const fin = e.fecha_fin?.split('T')[0];
                                const matchesDate = dateStr >= inicio && dateStr <= fin;
                                const matchesDept = calendarDeptFilter === "all" || e.departamento_id === calendarDeptFilter;
                                return matchesDate && matchesDept;
                              }) : [];
                              const isToday = day && day.toDateString() === new Date().toDateString();
                              
                              const getEventColor = (evento) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const inicio = new Date(evento.fecha_inicio);
                                inicio.setHours(0, 0, 0, 0);
                                const diffDays = Math.ceil((inicio - today) / (1000 * 60 * 60 * 24));
                                
                                if (evento.tipo === "permiso") return "bg-orange-500 text-white";
                                if (diffDays > 5 || diffDays < -5) return "bg-red-500 text-white";
                                return "bg-yellow-500 text-black";
                              };
                              
                              return (
                                <div 
                                  key={idx} 
                                  className={`min-h-[100px] border-b border-r p-1 ${
                                    !day ? 'bg-slate-50 dark:bg-slate-900' : ''
                                  } ${isToday ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                                >
                                  {day && (
                                    <>
                                      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600 font-bold' : 'text-slate-600'}`}>
                                        {day.getDate()}
                                      </div>
                                      <div className="space-y-1">
                                        {dayEvents.slice(0, 3).map((evento, eIdx) => (
                                          <div 
                                            key={eIdx}
                                            className={`text-xs px-1 py-0.5 rounded truncate ${getEventColor(evento)}`}
                                            title={`${evento.tipo === 'vacacion' ? 'Vacación' : 'Permiso'}: ${evento.user_name}`}
                                          >
                                            {evento.tipo === 'vacacion' ? '🏖️' : '📋'} {evento.user_name?.split(' ')[0]}
                                          </div>
                                        ))}
                                        {dayEvents.length > 3 && (
                                          <div className="text-xs text-slate-500 pl-1">+{dayEvents.length - 3} más</div>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* New Request Dialog */}
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedTipoSolicitud === "permiso" && <><FileText className="w-5 h-5 text-blue-500" /> Nueva Solicitud de Permiso</>}
                {selectedTipoSolicitud === "cumpleanos" && <><Cake className="w-5 h-5 text-pink-500" /> Solicitar Día de Cumpleaños</>}
                {selectedTipoSolicitud === "tardanza" && <><Clock className="w-5 h-5 text-amber-500" /> Reportar Tardanza</>}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* User Info Display */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Nombre</p>
                  <p className="font-medium text-sm text-slate-900 dark:text-white">{user?.name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Departamento</p>
                  <p className="font-medium text-sm text-slate-900 dark:text-white">{user?.departamento_nombre || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Supervisor</p>
                  <p className="font-medium text-sm text-slate-900 dark:text-white">{user?.supervisor_nombre || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Cargo</p>
                  <p className="font-medium text-sm text-slate-900 dark:text-white">{user?.cargo || user?.role || "-"}</p>
                </div>
              </div>

              {/* Permiso Form */}
              {selectedTipoSolicitud === "permiso" && (
                <>
                  <div>
                    <Label>Tipo de Permiso *</Label>
                    <Select value={formData.tipo_permiso} onValueChange={(v) => setFormData({...formData, tipo_permiso: v})}>
                      <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiposPermiso.map(tipo => (
                          <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fecha Inicio *</Label>
                      <Input
                        type="date"
                        value={formData.fecha_inicio}
                        onChange={(e) => {
                          setFormData({
                            ...formData, 
                            fecha_inicio: e.target.value,
                            fecha_fin: e.target.value
                          });
                        }}
                        className="dark:bg-slate-700 dark:border-slate-600"
                      />
                    </div>
                    <div>
                      <Label>Fecha Fin</Label>
                      <Input
                        type="date"
                        value={formData.fecha_fin || formData.fecha_inicio}
                        disabled
                        className="dark:bg-slate-700 dark:border-slate-600 opacity-50"
                      />
                      <p className="text-xs text-slate-500 mt-1">Para permisos por horas</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Hora Inicio *</Label>
                      <Input
                        type="time"
                        value={formData.hora_inicio}
                        onChange={(e) => setFormData({...formData, hora_inicio: e.target.value})}
                        className="dark:bg-slate-700 dark:border-slate-600"
                        required
                      />
                    </div>
                    <div>
                      <Label>Hora Fin *</Label>
                      <Input
                        type="time"
                        value={formData.hora_fin}
                        onChange={(e) => setFormData({...formData, hora_fin: e.target.value})}
                        className="dark:bg-slate-700 dark:border-slate-600"
                        required
                      />
                    </div>
                  </div>

                  {/* Mostrar horas calculadas */}
                  {formData.hora_inicio && formData.hora_fin && (() => {
                    const [startH, startM] = formData.hora_inicio.split(':').map(Number);
                    const [endH, endM] = formData.hora_fin.split(':').map(Number);
                    const startMinutes = startH * 60 + startM;
                    const endMinutes = endH * 60 + endM;
                    const horasCalc = (endMinutes - startMinutes) / 60;
                    const isValid = horasCalc > 0 && horasCalc <= 4;
                    
                    return (
                      <div className={`p-3 rounded-lg ${isValid ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'}`}>
                        <p className={`text-sm font-medium ${isValid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                          {horasCalc > 0 ? (
                            <>
                              Horas solicitadas: <strong>{horasCalc.toFixed(1)} horas</strong>
                              {horasCalc > 4 && (
                                <span className="block text-xs mt-1">
                                  ⚠️ El permiso es máximo 4 horas. Si necesita más tiempo, debe solicitar un día de vacaciones.
                                </span>
                              )}
                            </>
                          ) : (
                            "La hora de fin debe ser mayor a la hora de inicio"
                          )}
                        </p>
                      </div>
                    );
                  })()}

                  <div>
                    <Label>Motivo</Label>
                    <Textarea
                      value={formData.motivo}
                      onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                      placeholder="Describa el motivo del permiso..."
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.requiere_compensacion}
                      onCheckedChange={(v) => setFormData({...formData, requiere_compensacion: v})}
                    />
                    <Label>Requiere compensación de tiempo</Label>
                  </div>
                </>
              )}

              {/* Cumpleaños Form */}
              {selectedTipoSolicitud === "cumpleanos" && (
                <div>
                  <Label>Fecha para tomar el día *</Label>
                  <Input
                    type="date"
                    value={formData.fecha_solicitada}
                    onChange={(e) => setFormData({...formData, fecha_solicitada: e.target.value})}
                    className="dark:bg-slate-700 dark:border-slate-600"
                  />
                  <p className="text-sm text-slate-500 mt-2">
                    Selecciona la fecha en que deseas tomar tu día libre por cumpleaños
                  </p>
                </div>
              )}

              {/* Tardanza Form */}
              {selectedTipoSolicitud === "tardanza" && (
                <>
                  <div>
                    <Label>Tipo *</Label>
                    <Select value={formData.tipo_tardanza} onValueChange={(v) => setFormData({...formData, tipo_tardanza: v})}>
                      <SelectTrigger className="dark:bg-slate-700 dark:border-slate-600">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tardanza">Tardanza</SelectItem>
                        <SelectItem value="ausencia_justificada">Ausencia Justificada</SelectItem>
                        <SelectItem value="ausencia_injustificada">Ausencia Injustificada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Fecha *</Label>
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>

                  {formData.tipo_tardanza === "tardanza" && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Hora Esperada</Label>
                          <Input
                            type="time"
                            value={formData.hora_esperada}
                            onChange={(e) => {
                              const esperada = e.target.value;
                              setFormData({
                                ...formData,
                                hora_esperada: esperada,
                                minutos_tarde: calculateMinutesLate(formData.hora_entrada, esperada)
                              });
                            }}
                            className="dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                        <div>
                          <Label>Hora de Entrada</Label>
                          <Input
                            type="time"
                            value={formData.hora_entrada}
                            onChange={(e) => {
                              const entrada = e.target.value;
                              setFormData({
                                ...formData,
                                hora_entrada: entrada,
                                minutos_tarde: calculateMinutesLate(entrada, formData.hora_esperada)
                              });
                            }}
                            className="dark:bg-slate-700 dark:border-slate-600"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Minutos de Tardanza: <span className="font-bold text-amber-600">{formData.minutos_tarde}</span></Label>
                      </div>
                    </>
                  )}

                  <div>
                    <Label>Motivo / Justificación</Label>
                    <Textarea
                      value={formData.justificacion}
                      onChange={(e) => setFormData({...formData, justificacion: e.target.value, motivo: e.target.value})}
                      placeholder="Explique el motivo..."
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>
                </>
              )}

              {/* Ausencia Justificada / Injustificada Form */}
              {(selectedTipoSolicitud === "ausencia_justificada" || selectedTipoSolicitud === "ausencia_injustificada") && (
                <>
                  <div className="p-3 rounded-lg bg-slate-100 dark:bg-slate-700">
                    <p className="text-sm font-medium">
                      Tipo: {selectedTipoSolicitud === "ausencia_justificada" ? "Ausencia Justificada" : "Ausencia Injustificada"}
                    </p>
                  </div>

                  <div>
                    <Label>Fecha de Ausencia *</Label>
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                      className="dark:bg-slate-700 dark:border-slate-600"
                    />
                  </div>

                  <div>
                    <Label>{selectedTipoSolicitud === "ausencia_justificada" ? "Motivo / Justificación *" : "Motivo"}</Label>
                    <Textarea
                      value={formData.justificacion}
                      onChange={(e) => setFormData({...formData, justificacion: e.target.value, motivo: e.target.value})}
                      placeholder={selectedTipoSolicitud === "ausencia_justificada" 
                        ? "Describa el motivo de la ausencia y adjunte documentación si es necesario..." 
                        : "Describa el motivo si aplica..."}
                      className="dark:bg-slate-700 dark:border-slate-600"
                      rows={4}
                    />
                  </div>

                  {selectedTipoSolicitud === "ausencia_justificada" && (
                    <p className="text-xs text-slate-500">
                      Nota: Las ausencias justificadas requieren documentación de soporte (certificado médico, constancia, etc.)
                    </p>
                  )}
                </>
              )}

              {/* Firma Digital Section - Required */}
              <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                <Label className="flex items-center gap-2 mb-3 text-slate-700 dark:text-slate-300">
                  <PenTool className="w-4 h-4" />
                  Firma Digital <span className="text-red-500">*</span>
                </Label>
                
                {!userFirma ? (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Firma no configurada</p>
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Debe configurar su firma digital antes de enviar solicitudes.
                        Vaya a <strong>Configuración → Firma Digital</strong> para cargar su firma.
                      </p>
                    </div>
                  </div>
                ) : firmaSolicitud ? (
                  <div className="flex items-center gap-4 p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                    <img 
                      src={userFirma} 
                      alt="Mi firma" 
                      className="h-12 object-contain border rounded bg-white"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">Solicitud firmada</span>
                      </div>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                        {user?.name} - {new Date().toLocaleDateString()}
                      </p>
                    </div>
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm"
                      onClick={() => setFirmaSolicitud(false)}
                      className="text-slate-500"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex-1 p-3 border border-dashed border-slate-300 dark:border-slate-500 rounded bg-white dark:bg-slate-800 text-center">
                      <p className="text-sm text-slate-500">Haga clic en "Firmar" para agregar su firma</p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => setFirmaSolicitud(true)}
                      className="gap-2 bg-blue-600 hover:bg-blue-700"
                    >
                      <PenTool className="w-4 h-4" />
                      Firmar
                    </Button>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowNewDialog(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || !firmaSolicitud || !userFirma} 
                  className="gap-2"
                  title={!userFirma ? "Configure su firma primero" : !firmaSolicitud ? "Debe firmar la solicitud" : ""}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Send className="w-4 h-4" />
                  Enviar Solicitud
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud</DialogTitle>
            </DialogHeader>
            {selectedSolicitud && (
              <div className="space-y-4">
                <Badge className={
                  selectedSolicitud.tipo_solicitud === "permiso" ? "bg-blue-100 text-blue-700" :
                  selectedSolicitud.tipo_solicitud === "cumpleanos" ? "bg-pink-100 text-pink-700" :
                  "bg-amber-100 text-amber-700"
                }>
                  {selectedSolicitud.tipo_label}
                </Badge>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Solicitante</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.user_name || selectedSolicitud.empleado_nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Departamento</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.user_departamento || selectedSolicitud.departamento_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Supervisor</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.supervisor_nombre || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Estado</p>
                    <Badge className={ESTADOS.find(e => e.value === selectedSolicitud.estado)?.color || "bg-slate-100 text-slate-700"}>
                      {ESTADOS.find(e => e.value === selectedSolicitud.estado)?.label || selectedSolicitud.estado}
                    </Badge>
                  </div>
                </div>

                {selectedSolicitud.tipo_solicitud === "permiso" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Fecha Inicio</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.fecha_inicio}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Fecha Fin</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.fecha_fin}</p>
                    </div>
                    {(selectedSolicitud.hora_inicio || selectedSolicitud.hora_fin) && (
                      <>
                        <div>
                          <p className="text-sm text-slate-500">Hora Desde</p>
                          <p className="font-medium dark:text-white">{selectedSolicitud.hora_inicio || "-"}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Hora Hasta</p>
                          <p className="font-medium dark:text-white">{selectedSolicitud.hora_fin || "-"}</p>
                        </div>
                      </>
                    )}
                    <div>
                      <p className="text-sm text-slate-500">Días</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.dias_solicitados}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Tipo Permiso</p>
                      <p className="font-medium dark:text-white">{getTipoPermisoLabel(selectedSolicitud.tipo_permiso)}</p>
                    </div>
                  </div>
                )}

                {selectedSolicitud.tipo_solicitud === "vacaciones" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Fecha Inicio</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.fecha_inicio || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Fecha Fin</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.fecha_fin || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Días Solicitados</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.dias_solicitados || 0} días</p>
                    </div>
                  </div>
                )}

                {selectedSolicitud.tipo_solicitud === "cumpleanos" && (
                  <div>
                    <p className="text-sm text-slate-500">Fecha Solicitada</p>
                    <p className="font-medium dark:text-white">{selectedSolicitud.fecha_solicitada}</p>
                  </div>
                )}

                {selectedSolicitud.tipo_solicitud === "tardanza" && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Fecha</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.fecha}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Hora Entrada</p>
                      <p className="font-medium dark:text-white">{selectedSolicitud.hora_entrada || "-"}</p>
                    </div>
                    {selectedSolicitud.minutos_tarde > 0 && (
                      <div>
                        <p className="text-sm text-slate-500">Minutos Tarde</p>
                        <p className="font-medium text-amber-600">{selectedSolicitud.minutos_tarde} min</p>
                      </div>
                    )}
                  </div>
                )}

                {(selectedSolicitud.motivo || selectedSolicitud.justificacion) && (
                  <div>
                    <p className="text-sm text-slate-500">Motivo</p>
                    <p className="dark:text-white">{selectedSolicitud.motivo || selectedSolicitud.justificacion}</p>
                  </div>
                )}

                {/* Display signatures */}
                <FirmasDisplay firmas={solicitudFirmas} />

                {/* Show action buttons for admins/supervisors */}
                {(isAdminOrGH || (isSupervisor && selectedSolicitud.supervisor_id === user?.id)) && (
                  <div className="flex gap-2 pt-4 border-t dark:border-slate-700">
                    {selectedSolicitud.estado === "pendiente" ? (
                      <Button 
                        onClick={() => {
                          setShowDetailDialog(false);
                          handleApprove(selectedSolicitud, selectedSolicitud.tipo_solicitud);
                        }} 
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <PenTool className="w-4 h-4 mr-2" />
                        Responder con Firma
                      </Button>
                    ) : (
                      <>
                        <div className="flex-1 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                          <p className="text-xs text-amber-700 dark:text-amber-300 mb-2">
                            Esta solicitud ya fue {selectedSolicitud.estado}. ¿Desea cambiar el estado?
                          </p>
                          <Button 
                            onClick={() => {
                              setShowDetailDialog(false);
                              handleApprove(selectedSolicitud, selectedSolicitud.tipo_solicitud);
                            }} 
                            variant="outline"
                            className="w-full border-amber-500 text-amber-600 hover:bg-amber-100"
                          >
                            <PenTool className="w-4 h-4 mr-2" />
                            Cambiar Estado
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            
            {selectedSolicitud && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    ¿Está seguro que desea eliminar esta solicitud? Esta acción no se puede deshacer.
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Tipo</p>
                    <p className="font-medium">{selectedSolicitud.tipo_solicitud || selectedSolicitud.tipo || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-slate-500 dark:text-slate-400">Estado</p>
                    <Badge className={`text-xs ${
                      selectedSolicitud.estado === "pendiente" ? "bg-amber-100 text-amber-700" :
                      selectedSolicitud.estado === "aprobada" ? "bg-green-100 text-green-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      {selectedSolicitud.estado || "N/A"}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500 dark:text-slate-400">Empleado</p>
                    <p className="font-medium">{selectedSolicitud.user_name || selectedSolicitud.empleado_nombre || "N/A"}</p>
                  </div>
                  {selectedSolicitud.motivo && (
                    <div className="col-span-2">
                      <p className="text-slate-500 dark:text-slate-400">Motivo</p>
                      <p className="text-sm">{selectedSolicitud.motivo}</p>
                    </div>
                  )}
                </div>

                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                    Cancelar
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={() => handleDeleteSolicitud(selectedSolicitud)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approval with Signature Dialog */}
        <AprobarConFirmaDialog
          open={showApprovalDialog}
          onOpenChange={setShowApprovalDialog}
          solicitud={selectedSolicitud}
          tipoSolicitud={selectedSolicitud?.tipo_solicitud}
          onComplete={() => {
            fetchData();
            setShowDetailDialog(false);
          }}
        />
      </main>
    </div>
  );
}
